import { CategoryType } from '@app/domain/entities';
import {
  CountAccountTransfersUseCase,
  CountExpensesUseCase,
  CountIncomesUseCase,
  GetAccountBalanceUseCase,
  GetAccountsUseCase,
  GetAccountTransfersUseCase,
  GetCategoriesUseCase,
  GetCurrenciesUseCase,
  GetCurrencyExchangesUseCase,
  GetExpensesUseCase,
  GetIncomesUseCase,
  GetTagsUseCase,
} from '@app/domain/use-cases';
import {
  financeToolArgumentsSchemas,
  financeToolOutputSchemas,
  type FinanceCommand,
  type JsonValue,
} from '@app/platform/ai-session-protocol';

export class FinanceToolRegistry {
  constructor(
    private readonly getAccounts: GetAccountsUseCase,
    private readonly getAccountBalance: GetAccountBalanceUseCase,
    private readonly getCategories: GetCategoriesUseCase,
    private readonly getCurrencies: GetCurrenciesUseCase,
    private readonly getTags: GetTagsUseCase,
    private readonly getExpenses: GetExpensesUseCase,
    private readonly countExpenses: CountExpensesUseCase,
    private readonly getIncomes: GetIncomesUseCase,
    private readonly countIncomes: CountIncomesUseCase,
    private readonly getTransfers: GetAccountTransfersUseCase,
    private readonly countTransfers: CountAccountTransfersUseCase,
    private readonly getExchanges: GetCurrencyExchangesUseCase,
  ) {}

  async execute(command: FinanceCommand): Promise<JsonValue> {
    const args = financeToolArgumentsSchemas[command.name].parse(command.arguments);
    let result: unknown;

    switch (command.name) {
      case 'list_accounts': {
        const accounts = await this.getAccounts.execute({ withHidden: false });
        result = { accounts: accounts.map(({ id, name }) => ({ id, name })) };
        break;
      }
      case 'list_balances': {
        const query = financeToolArgumentsSchemas.list_balances.parse(args);
        const [accounts, { currencies }] = await Promise.all([
          this.getAccounts.execute({ withHidden: false }),
          this.getCurrencies.execute({ includeHidden: true }),
        ]);
        const defaultCurrencyCode = this.requireDefaultCurrencyCode(currencies);
        const selectedAccounts = accounts.filter((account) => account.id === query.accountId);
        const visibleCurrencies = currencies.filter((currency) => !currency.hidden);
        const selectedCurrencies = query.currencyCode ? [{ code: query.currencyCode }] : visibleCurrencies;
        const balances = (
          await Promise.all(
            selectedAccounts.flatMap((account) =>
              selectedCurrencies.map(async (currency) => {
                const balance = await this.getAccountBalance.execute(account.id, currency.code);
                return {
                  account: { id: account.id, name: account.name },
                  currencyCode: currency.code,
                  amount: balance.amount,
                  incomes: balance.incomes,
                  expenses: balance.expenses,
                };
              }),
            ),
          )
        ).filter((row) => row.amount !== 0 || row.incomes !== 0 || row.expenses !== 0);
        result = { defaultCurrencyCode, balances };
        break;
      }
      case 'list_categories': {
        const { categories } = await this.getCategories.execute();
        const visible = categories.filter(
          (category) =>
            !category.hidden &&
            (category.categoryType === CategoryType.Income || category.categoryType === CategoryType.Expense),
        );
        const byId = new Map(visible.map((category) => [category.id, category]));
        result = {
          categories: visible.map((category) => ({
            id: category.id,
            name: category.name,
            parentId: category.parentId,
            ...(category.parentId ? { parentName: byId.get(category.parentId)?.name } : {}),
            type: category.categoryType === CategoryType.Income ? ('income' as const) : ('expense' as const),
          })),
        };
        break;
      }
      case 'list_currencies': {
        const { currencies } = await this.getCurrencies.execute({ includeHidden: true });
        result = {
          defaultCurrencyCode: this.requireDefaultCurrencyCode(currencies),
          currencies: currencies
            .filter((currency) => !currency.hidden)
            .map((currency) => ({
              code: currency.code,
              name: currency.name,
              isDefault: currency.isDefault,
            })),
        };
        break;
      }
      case 'list_tags': {
        const { tags } = await this.getTags.execute();
        result = { tags: tags.map(({ id, name }) => ({ id, name })) };
        break;
      }
      case 'query_expenses': {
        const query = this.withDateDefaults(financeToolArgumentsSchemas.query_expenses.parse(args));
        const filter = {
          ...this.toDateFilter(query),
          ...(query.accountId ? { accountId: query.accountId } : {}),
          ...(query.currencyCode ? { currencyCode: query.currencyCode } : {}),
          ...(query.includeCategoryIds ? { includeCategoryIds: query.includeCategoryIds } : {}),
          ...(query.excludeCategoryIds ? { excludeCategoryIds: query.excludeCategoryIds } : {}),
          ...(query.amountFrom !== undefined ? { amountFrom: query.amountFrom } : {}),
          ...(query.amountTo !== undefined ? { amountTo: query.amountTo } : {}),
          ...(query.noteContains ? { note: query.noteContains } : {}),
          ...(query.includeTagIds ? { includeTagIds: query.includeTagIds } : {}),
          ...(query.excludeTagIds ? { excludeTagIds: query.excludeTagIds } : {}),
        };
        const [{ expenses }, totalCount, tagsResult, currenciesResult] = await Promise.all([
          this.getExpenses.execute({ offset: query.offset, limit: query.limit }, filter),
          this.countExpenses.execute(filter),
          this.getTags.execute(),
          this.getCurrencies.execute({ includeHidden: true }),
        ]);
        const tagsById = new Map(tagsResult.tags.map((tag) => [tag.id, tag.name]));
        const items = expenses.map((expense) => ({
          id: expense.id,
          date: this.toIsoDate(expense.date),
          account: { id: expense.accountId, name: expense.accountName },
          category: {
            id: expense.categoryId,
            name: expense.categoryName,
            parentId: expense.parentCategoryId ?? null,
            ...(expense.parentCategoryName ? { parentName: expense.parentCategoryName } : {}),
            type: 'expense' as const,
          },
          amount: expense.amount,
          currencyCode: expense.currencyCode,
          rate: expense.rate,
          amountInDefaultCurrency: expense.amount * expense.rate,
          note: expense.note,
          tags: (expense.tagIds ?? []).map((id) => ({ id, name: tagsById.get(id) ?? `Tag ${id}` })),
          ...(expense.mcc ? { mcc: expense.mcc } : {}),
        }));
        result = this.page(
          items,
          totalCount,
          query.offset,
          this.requireDefaultCurrencyCode(currenciesResult.currencies),
        );
        break;
      }
      case 'query_incomes': {
        const query = this.withDateDefaults(financeToolArgumentsSchemas.query_incomes.parse(args));
        const filter = {
          ...this.toDateFilter(query),
          ...(query.accountId ? { accountId: query.accountId } : {}),
          ...(query.currencyCode ? { currencyCode: query.currencyCode } : {}),
          ...(query.includeCategoryIds ? { includeCategoryIds: query.includeCategoryIds } : {}),
          ...(query.excludeCategoryIds ? { excludeCategoryIds: query.excludeCategoryIds } : {}),
          ...(query.noteContains ? { note: query.noteContains } : {}),
        };
        const [{ incomes }, totalCount, currenciesResult] = await Promise.all([
          this.getIncomes.execute({ offset: query.offset, limit: query.limit }, filter),
          this.countIncomes.execute(filter),
          this.getCurrencies.execute({ includeHidden: true }),
        ]);
        const items = incomes.map((income) => ({
          id: income.id,
          date: this.toIsoDate(income.date),
          account: { id: income.accountId, name: income.accountName },
          category: {
            id: income.categoryId,
            name: income.categoryName,
            parentId: income.parentCategoryId ?? null,
            ...(income.parentCategoryName ? { parentName: income.parentCategoryName } : {}),
            type: 'income' as const,
          },
          amount: income.amount,
          currencyCode: income.currencyCode,
          rate: income.rate,
          amountInDefaultCurrency: income.amount * income.rate,
          note: income.note,
        }));
        result = this.page(
          items,
          totalCount,
          query.offset,
          this.requireDefaultCurrencyCode(currenciesResult.currencies),
        );
        break;
      }
      case 'query_account_transfers': {
        const query = this.withDateDefaults(financeToolArgumentsSchemas.query_account_transfers.parse(args));
        const filter = {
          ...this.toDateFilter(query),
          ...(query.sourceAccountId ? { accountOutId: query.sourceAccountId } : {}),
          ...(query.destinationAccountId ? { accountInId: query.destinationAccountId } : {}),
          ...(query.currencyCode ? { currencyCode: query.currencyCode } : {}),
          showHiddenAccounts: true,
        };
        const [{ accountTransfers }, totalCount, currenciesResult] = await Promise.all([
          this.getTransfers.execute({ offset: query.offset, limit: query.limit }, filter),
          this.countTransfers.execute(filter),
          this.getCurrencies.execute({ includeHidden: true }),
        ]);
        const items = accountTransfers.map((transfer) => ({
          id: transfer.id,
          date: this.toIsoDate(transfer.date),
          sourceAccount: { id: transfer.accountOutId, name: transfer.accountOutName },
          destinationAccount: { id: transfer.accountInId, name: transfer.accountInName },
          amount: transfer.amount,
          currencyCode: transfer.currencyCode,
          note: transfer.note,
        }));
        result = this.page(
          items,
          totalCount,
          query.offset,
          this.requireDefaultCurrencyCode(currenciesResult.currencies),
        );
        break;
      }
      case 'query_currency_exchanges': {
        const query = this.withDateDefaults(financeToolArgumentsSchemas.query_currency_exchanges.parse(args));
        const filter = {
          ...this.toDateFilter(query),
          ...(query.accountId ? { accountId: query.accountId } : {}),
          ...(query.fromCurrencyCode ? { fromCurrencyCode: query.fromCurrencyCode } : {}),
          ...(query.toCurrencyCode ? { toCurrencyCode: query.toCurrencyCode } : {}),
          showHiddenCurrencies: true,
        };
        const [{ exchanges, total }, currenciesResult] = await Promise.all([
          this.getExchanges.execute({
            cursor: { offset: query.offset, limit: query.limit },
            filter,
          }),
          this.getCurrencies.execute({ includeHidden: true }),
        ]);
        const items = exchanges.map((exchange) => ({
          id: exchange.id,
          date: this.toIsoDate(exchange.date),
          account: { id: exchange.accountId, name: exchange.accountName },
          fromAmount: exchange.fromAmount,
          fromCurrencyCode: exchange.fromCurrencyCode,
          toAmount: exchange.toAmount,
          toCurrencyCode: exchange.toCurrencyCode,
          note: exchange.note,
        }));
        result = this.page(items, total, query.offset, this.requireDefaultCurrencyCode(currenciesResult.currencies));
        break;
      }
    }

    return financeToolOutputSchemas[command.name].parse(result) as JsonValue;
  }

  private withDateDefaults<T extends { dateFrom?: string; dateTo?: string }>(query: T): T {
    if (query.dateFrom && query.dateTo) return query;
    const end = this.utcToday();
    if (query.dateFrom) {
      return { ...query, dateTo: this.toIsoDate(end) };
    }
    if (query.dateTo) return query;
    const start = new Date(end);
    start.setUTCDate(start.getUTCDate() - 29);
    return {
      ...query,
      dateFrom: this.toIsoDate(start),
      dateTo: this.toIsoDate(end),
    };
  }

  private utcToday(): Date {
    const today = new Date();
    return new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  }

  private toDateFilter(query: { dateFrom?: string; dateTo?: string }): { dateFrom?: Date; dateTo?: Date } {
    return {
      ...(query.dateFrom ? { dateFrom: new Date(`${query.dateFrom}T00:00:00.000Z`) } : {}),
      ...(query.dateTo ? { dateTo: this.dayAfter(query.dateTo) } : {}),
    };
  }

  private dayAfter(date: string): Date {
    const result = new Date(`${date}T00:00:00.000Z`);
    result.setUTCDate(result.getUTCDate() + 1);
    return result;
  }

  private toIsoDate(date: Date): string {
    return date.toISOString().slice(0, 10);
  }

  private requireDefaultCurrencyCode(currencies: { code: string; isDefault: boolean }[]): string {
    const defaultCurrency = currencies.find((currency) => currency.isDefault);
    if (!defaultCurrency) {
      throw new Error('Workspace has no default currency');
    }
    return defaultCurrency.code;
  }

  private page<T>(items: T[], totalCount: number, offset: number, defaultCurrencyCode: string) {
    const nextOffset = offset + items.length;
    const hasMore = nextOffset < totalCount;
    return {
      items,
      defaultCurrencyCode,
      totalCount,
      hasMore,
      nextOffset: hasMore ? nextOffset : null,
    };
  }
}
